import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { WarehouseEffects } from "../../State/WarehouseEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { Warehouse } from "../../Dto/WarehouseModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-warehouse-edit",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, LocationSelectorComponent, RichTextEditorComponent],
  templateUrl: "./WarehouseEditPage.html",
  styleUrl: "./WarehouseEditPage.scss",
})
export class WarehouseEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly warehouseEffects = inject(WarehouseEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly warehouseId = this.route.snapshot.paramMap.get("id") ?? undefined;

  warehouseForm: FormGroup;
  warehouse: Warehouse | null = null;
  isSaving = false;

  constructor() {
    this.warehouseForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      note: [""],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        longitude: [0],
        latitude: [0],
      }),
    });
  }

  get canSaveEntity(): boolean {
    return !this.isSaving && !!this.warehouseId && this.warehouseForm.valid;
  }

  ngOnInit(): void {
    if (!this.warehouseId) {
      this.router.navigate(["/warehouses"]).catch(() => undefined);
      return;
    }

    this.fetchWarehouse();
  }

  private async fetchWarehouse(): Promise<void> {
    if (!this.warehouseId) {
      this.toastEffects.show("Warehouse ID is required.", "error");
      await this.router.navigate(["/warehouses"]);
      return;
    }

    const warehouse = await this.warehouseEffects.getWarehouseById(this.warehouseId);
    if (!warehouse) {
      this.toastEffects.show("Warehouse not found.", "error");
      await this.router.navigate(["/warehouses"]);
      return;
    }

    setTimeout(() => {
      this.warehouse = warehouse;
      this.patchFormFromWarehouse(warehouse);
      this.cdr.detectChanges();
    }, 0);
  }

  async saveWarehouse(): Promise<void> {
    if (this.warehouseForm.invalid) {
      this.warehouseForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    if (!this.warehouseId) {
      this.toastEffects.show("Missing warehouse identifier.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();

    const formData = this.warehouseForm.value;
    const updateData = {
      name: formData.name,
      note: (() => {
        if (typeof formData.note === "string") {
          return formData.note.trim();
        }
        return "";
      })(),
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        longitude: 0,
        latitude: 0,
      },
    };

    try {
      const updatedWarehouse = await this.warehouseEffects.updateWarehouse(this.warehouseId, updateData);

      setTimeout(() => {
        this.warehouse = updatedWarehouse;
        this.patchFormFromWarehouse(updatedWarehouse);
        this.toastEffects.show("Warehouse updated successfully.", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update warehouse."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  goBack(): void {
    this.router.navigate(["/warehouses"]).catch(() => undefined);
  }

  onAddressChange(value: LocationAddress): void {
    this.warehouseForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
      },
    });
  }

  private patchFormFromWarehouse(warehouse: Warehouse): void {
    if (this.warehouseForm.dirty) {
      return;
    }

    this.warehouseForm.patchValue(
      {
        name: warehouse.name,
        note: warehouse.note ?? "",
        address: {
          stateProvinceRegion: warehouse.address.stateProvinceRegion,
          countyDistrict: warehouse.address.countyDistrict,
          subdistrictMunicipality: warehouse.address.subdistrictMunicipality,
          communeVillage: warehouse.address.communeVillage,
          line: warehouse.address.line || "",
          zip: warehouse.address.zip || "",
          longitude: warehouse.address.longitude || 0,
          latitude: warehouse.address.latitude || 0,
        },
      },
      { emitEvent: false },
    );
  }
}
